/**
 * useGoogleMaps3D — carga del SDK de Google Maps JavaScript API (beta) con
 * la librería `maps3d`, que expone el Custom Element `<gmp-map-3d>`
 * (Photorealistic 3D Tiles).
 *
 * Notas:
 *  - El SDK se carga **una sola vez** por sesión de pestaña. Una promesa
 *    singleton evita scripts duplicados si varios componentes intentan
 *    activarlo en paralelo.
 *  - El callback se nombra `__initGoogleMaps3DSDK__` y se limpia tras
 *    resolver para no dejar globals colgando.
 *  - Si `VITE_GOOGLE_3D_KEY` no existe (no configurado en `.env`), el hook
 *    devuelve `error` y el botón del header oculta el modo en `App.tsx`.
 *  - El TOS de Google obliga a mostrar atribución "Map data © Google" — el
 *    Custom Element `<gmp-map-3d>` añade su propio control con el logo de
 *    Google, así que la cumplimos por defecto sin código extra.
 */
import { useEffect, useState } from 'react';

const SCRIPT_ID = 'google-maps-3d-sdk';
const CALLBACK = '__initGoogleMaps3DSDK__';

declare global {
  interface Window {
    google?: any;
    [CALLBACK]?: () => void;
  }
}

let loaderPromise: Promise<void> | null = null;

function loadSDK(apiKey: string): Promise<void> {
  if (loaderPromise) return loaderPromise;

  loaderPromise = new Promise<void>((resolve, reject) => {
    // Otro componente / hook ya inyectó el script — engancharse a sus eventos.
    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      if (window.google?.maps?.importLibrary) return resolve();
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('SDK script load error')));
      return;
    }

    window[CALLBACK] = () => {
      try { delete window[CALLBACK]; } catch { window[CALLBACK] = undefined; }
      resolve();
    };

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.async = true;
    script.defer = true;
    script.src =
      `https://maps.googleapis.com/maps/api/js` +
      `?key=${encodeURIComponent(apiKey)}` +
      `&v=beta&libraries=maps3d&callback=${CALLBACK}`;
    script.onerror = () => reject(new Error('No se pudo cargar Google Maps 3D SDK'));
    document.head.appendChild(script);
  });

  return loaderPromise;
}

interface SDKState {
  loaded: boolean;
  error: string | null;
}

export function useGoogleMaps3D(): SDKState {
  const [state, setState] = useState<SDKState>({ loaded: false, error: null });

  useEffect(() => {
    const apiKey = import.meta.env.VITE_GOOGLE_3D_KEY as string | undefined;
    if (!apiKey) {
      setState({ loaded: false, error: 'VITE_GOOGLE_3D_KEY no configurada' });
      return;
    }

    let cancelled = false;
    loadSDK(apiKey)
      .then(async () => {
        try {
          // maps3d trae Map3DElement, Marker3DElement, Polyline3DElement…
          // marker trae PinElement, que sirve como glyph custom del marker.
          await Promise.all([
            window.google.maps.importLibrary('maps3d'),
            window.google.maps.importLibrary('marker'),
          ]);
          if (!cancelled) setState({ loaded: true, error: null });
        } catch (e) {
          if (!cancelled) {
            setState({ loaded: false, error: 'no se pudo cargar maps3d/marker' });
          }
        }
      })
      .catch((e: Error) => {
        if (!cancelled) setState({ loaded: false, error: e.message });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}

export function isGoogle3DConfigured(): boolean {
  return Boolean(import.meta.env.VITE_GOOGLE_3D_KEY);
}
